import { groupBy, identity, maxBy } from "lodash";
import { RadioFieldModel, StringFieldModel } from "../../common/commonForms";
import { CompanyItem, UserItem } from "../../common/common";
import { TMessage } from "../../i18n/tt";
import { makeAutoObservable } from "mobx";
import { CustomRole, CustomRoleItem, CustomRoleLanguage } from "../../common/customRole";
import {
  DEFAULT_LANG,
  Language,
  languageLabels,
  supportedBackendLanguages,
} from "../../i18n/languages";
import { adminPermissionsSettings, customRoleCompanyUrl } from "../../utils/navigation";
import { ApiService } from "../../api";
import { findInitialUrl } from "../../common/customRoleNavigation";
import {
  confirmAction,
  EventDispatcher,
  loadUrl,
  navigate,
  showAlert,
  showGeneralError,
  thunk,
} from "../../events/eventsDomain";
import {
  extractPortfolioCustomRoleDetailsUrl,
  extractPortfolioCustomRoleUrl,
  portfolioApi,
  SelectedPortfolioOption,
} from "../portfolioDomain";
import { EditEmailForm } from "./contactsDomain";
import { User, UserCompany } from "../../types/UserInfo";
import { toDate } from "../../utils/Dates";

export type AddCompaniesToCustomRoleRequest = {
  customRoleId: string;
  companies: string[];
};

export type RemoveCompanyAccessFromCompanyRole = {
  userId: string;
  customRoleId: string;
  companies: string[];
};

export class CustomRoleStore {
  customRoleItem: CustomRoleItem;
  availableCompanies: CompanyItem[];
  customRoleSearchModel: StringFieldModel;
  dispatch: EventDispatcher;
  readonly apiService: ApiService;
  companyFilter: RadioFieldModel<CompanyItem | null>;
  selectedPortfolioOption: SelectedPortfolioOption;
  locale: Language;
  showEmailField: boolean;
  isLoadingPreview?: boolean;
  isLoadingInvite: boolean;
  revokingAccess?: string;
  user: User | null = null;
  refreshUserInformation: () => void;
  externalId?: string;
  creatorCompany?: UserCompany;

  constructor({
    customRoleItem,
    availableCompanies,
    dispatch,
    apiService,
    selectedPortfolioOption,
    locale,
    showEmailField,
    user,
    refreshUserInformation,
    externalId,
    creatorCompany,
  }: {
    customRoleItem: CustomRoleItem;
    availableCompanies: CompanyItem[];
    dispatch: EventDispatcher;
    apiService: ApiService;
    selectedPortfolioOption: SelectedPortfolioOption;
    locale: Language;
    showEmailField: boolean;
    user: User | null;
    refreshUserInformation: () => void;
    externalId?: string;
    creatorCompany?: UserCompany;
  }) {
    this.customRoleItem = customRoleItem;
    this.availableCompanies = availableCompanies;
    this.customRoleSearchModel = new StringFieldModel();
    this.dispatch = dispatch;
    this.apiService = apiService;
    this.selectedPortfolioOption = selectedPortfolioOption;
    this.locale = locale;
    this.companyFilter = new RadioFieldModel<CompanyItem | null>({
      value: null,
      options: this.customRoleItem?.companies ?? [],
      idFn: (c) => (c ? c.organisationNumber : "all"),
      labelFn: (c) => (c ? { translated: c.companyName } : { key: "portfolio.all-companies" }),
    });
    this.isLoadingInvite = false;
    this.showEmailField = showEmailField;
    this.user = user;
    this.refreshUserInformation = refreshUserInformation;
    this.externalId = externalId;
    this.creatorCompany = creatorCompany;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get hasAdminInCreatorCompany() {
    return this.creatorCompany !== undefined;
  }

  get customRoleId(): string {
    return this.customRoleItem.role.customRoleId;
  }

  get customRoleName(): string {
    return getRoleNameByLanguage(this.customRoleItem.role, this.locale) ?? "";
  }

  get showEmptyState(): boolean {
    return (this.customRoleItem?.availableUsers?.length ?? 0) === 0;
  }

  get description(): string | undefined {
    return getDescriptionByLanguage(this.customRoleItem.role, this.locale);
  }

  get users(): UserItem[] {
    return this.customRoleItem.availableUsers;
  }

  get prepCustomRoleUsers() {
    return prepCustomRoleUsersTableData({
      users: filterCustomRoleUsers({
        users: this.customRoleItem.availableUsers,
        companies: this.customRoleItem.companies,
        freetextFilter: this.customRoleSearchModel.inputValue,
        companyFilter: this.companyFilter.value,
      }),
      companies: this.customRoleItem.companies,
    });
  }

  get selectedUserCompanyItems(): CompanyItem[] {
    return this.externalId
      ? (this.prepCustomRoleUsers.find((f) => f.externalId === this.externalId)?.companies ?? [])
      : [];
  }

  userName(externalId: string): string {
    return this.customRoleItem.users.find((u) => u.externalId === externalId)?.name ?? "";
  }

  userEmail(externalId: string): string {
    return this.customRoleItem.users.find((u) => u.externalId === externalId)?.email ?? "";
  }

  previewCustomRole() {
    this.fetchPreviewCustomRole(this.customRoleItem.role.orgNo, this.customRoleItem.role);
  }

  closeDialog() {
    this.dispatch([{ type: "closeDialog" }]);
  }

  roleDetailsDialog() {
    this.dispatch([
      {
        type: "show-dialog",
        dialog: {
          type: "open-role-details",
          model: new CustomRoleDetailsModel({
            hasAdminInCreatorCompany: this.hasAdminInCreatorCompany,
            customRoleItem: this.customRoleItem,
            onClose: this.closeDialog,
            locale: this.locale,
          }),
        },
      },
    ]);
  }

  get emailValue(): string {
    return this.customRoleItem.users.find((f) => f.externalId === this.externalId)?.email ?? "";
  }

  openInviteCustomDialog() {
    this.dispatch([
      {
        type: "show-dialog",
        dialog: {
          type: "invite-custom-role-user",
          model: new InviteUserToCustomRoleModel({
            customRoleItem: this.customRoleItem,
            availableCompanies: this.availableCompanies,
            apiService: this.apiService,
            locale: this.locale,
            dispatch: this.dispatch,
            isLoadingInvite: this.isLoadingInvite,
            showEmailField: this.showEmailField,
            email: this.showEmailField ? undefined : this.emailValue,
            hasAdminInCreatorCompany: this.hasAdminInCreatorCompany,
            selectedUserCompanyItems: this.selectedUserCompanyItems,
            userName: this.userName(this.externalId ?? ""),
            portfolioName: this.selectedPortfolioOption.label,
          }),
        },
      },
    ]);
  }

  navigateToCustomRoleDetails(externalId: string) {
    this.dispatch([
      {
        type: "navigate",
        url: extractPortfolioCustomRoleDetailsUrl(
          this.selectedPortfolioOption,
          this.customRoleId,
          externalId,
        ),
      },
    ]);
  }

  revoke(externalId: string, orgNo?: string) {
    this.revokingAccess = orgNo ?? "all";
    const orgNos = orgNo ? [orgNo] : this.selectedUserCompanyItems.map((p) => p.organisationNumber);
    const body: RemoveCompanyAccessFromCompanyRole = {
      userId: externalId,
      customRoleId: this.customRoleId,
      companies: orgNos,
    };
    const onSuccess = () => {
      return [
        thunk((dispatch) =>
          orgNo && this.selectedUserCompanyItems.length > 1
            ? this.onRevokeCompanySuccess(orgNos)
            : dispatch([
                navigate(
                  extractPortfolioCustomRoleUrl(
                    this.selectedPortfolioOption,
                    this.customRoleItem.role.customRoleId,
                  ),
                ),
              ]),
        ),
        showAlert({
          key: "contacts.revoke.success.message",
        }),
      ];
    };
    this.dispatch(
      confirmAction({
        title: { key: "settings.revokeAdminAccess" },
        text: {
          key: "settings.revokeAdminAccessForQuestion",
          params: { name: this.userName(externalId) },
        },
        onCancel: [thunk(() => (this.revokingAccess = undefined))],
        onConfirm: [
          portfolioApi.removeCompanyAccessFromCustomRole({
            body,
            onSuccess,
            onFailure: () => [
              thunk((dispatch) => {
                dispatch([showGeneralError()]);
                this.revokingAccess = undefined;
              }),
            ],
          }),
        ],
      }),
    );
  }

  onRevokeCompanySuccess(orgNos: string[]) {
    this.customRoleItem.availableUsers = this.customRoleItem.availableUsers.filter((item) =>
      orgNos.find((f) => f !== item.orgNo),
    );
    this.revokingAccess = undefined;
  }

  setUserEmail(externalId: string, updateEmail: string) {
    const user = this.customRoleItem.users.find((u) => u.externalId === externalId);

    if (user) {
      user.email = updateEmail;
    }
  }

  editEmail(externalId: string) {
    this.dispatch([
      {
        type: "show-dialog",
        dialog: {
          type: "editEmailAdmin",
          model: new EditEmailForm({
            apiService: this.apiService,
            dispatch: this.dispatch,
            externalId,
            email: this.userEmail(externalId),
            onSuccess: (updateEmail) => this.setUserEmail(externalId, updateEmail),
            closeEditDialog: this.closeDialog,
            adminName: this.userName(externalId),
            customRoleId: this.customRoleId,
            user: this.user,
            refreshUserInformation: this.refreshUserInformation,
          }),
        },
      },
    ]);
  }

  *fetchPreviewCustomRole(orgNo: string, role: CustomRole) {
    this.isLoadingPreview = true;
    try {
      const roleId = role.customRoleId;
      yield this.apiService.openPreviewCustomRole(roleId, orgNo);
      const baseUrl = findInitialUrl({ orgNo, role });
      this.dispatch([
        loadUrl(
          customRoleCompanyUrl({
            roleId,
            baseUrl,
          }),
        ),
      ]);
    } finally {
      this.isLoadingPreview = false;
    }
  }
}

export class CustomRoleDetailsModel {
  hasAdminInCreatorCompany: boolean;
  customRoleItem: CustomRoleItem;
  onClose: () => void;
  locale: Language;

  constructor({
    hasAdminInCreatorCompany,
    customRoleItem,
    onClose,
    locale,
  }: {
    hasAdminInCreatorCompany: boolean;
    customRoleItem: CustomRoleItem;
    onClose: () => void;
    locale: Language;
  }) {
    this.hasAdminInCreatorCompany = hasAdminInCreatorCompany;
    this.customRoleItem = customRoleItem;
    this.onClose = onClose;
    this.locale = locale;
    makeAutoObservable(this);
  }

  get roleName(): string {
    return getRoleNameByLanguage(this.customRoleItem.role, this.locale) ?? "";
  }

  get description(): string | undefined {
    return getDescriptionByLanguage(this.customRoleItem.role, this.locale);
  }

  get companyName(): string {
    return (
      this.customRoleItem.companies.find(
        (p) => this.customRoleItem?.role.orgNo === p.organisationNumber,
      )?.companyName ?? ""
    );
  }

  get roleSettingsUrl() {
    const orgNo: string =
      this.customRoleItem.companies.find(
        (p) => this.customRoleItem?.role.orgNo === p.organisationNumber,
      )?.organisationNumber ?? "";
    return adminPermissionsSettings(orgNo);
  }

  get permissionMessages(): TMessage {
    return this.hasAdminInCreatorCompany
      ? {
          key: "portfolioContacts.custom-roles.details-dialog-role-can-edit-permission-text",
          params: {
            companyName: this.companyName,
            roleSettingsUrl: this.roleSettingsUrl,
          },
        }
      : {
          key: "portfolioContacts.custom-roles.details-dialog-role-edit-permission-text",
          params: {
            companyName: this.companyName,
            roleName: this.roleName,
          },
        };
  }
}

export class InviteUserToCustomRoleModel {
  customRoleItem: CustomRoleItem;
  email: StringFieldModel;
  language: RadioFieldModel<Language>;
  availableCompanies: CompanyItem[];
  selectedCompanies: string[] = [];
  private apiService: ApiService;
  locale: Language;
  dispatch: EventDispatcher;
  isLoadingInvite: boolean;
  showEmailField: boolean;
  enablingCompany?: string;
  hasAdminInCreatorCompany: boolean;
  selectedUserCompanyItems: CompanyItem[];
  userName: string;
  portfolioName: TMessage;

  constructor({
    customRoleItem,
    email,
    availableCompanies,
    apiService,
    locale,
    dispatch,
    isLoadingInvite,
    showEmailField,
    hasAdminInCreatorCompany,
    selectedUserCompanyItems,
    userName,
    portfolioName,
  }: {
    customRoleItem: CustomRoleItem;
    email?: string;
    availableCompanies: CompanyItem[];
    apiService: ApiService;
    locale: Language;
    dispatch: EventDispatcher;
    isLoadingInvite: boolean;
    showEmailField: boolean;
    hasAdminInCreatorCompany: boolean;
    selectedUserCompanyItems: CompanyItem[];
    userName: string;
    portfolioName: TMessage;
  }) {
    this.customRoleItem = customRoleItem;
    this.email = new StringFieldModel({
      value: email,
      required: true,
    });
    this.language = new RadioFieldModel<Language>({
      value: DEFAULT_LANG,
      options: supportedBackendLanguages,
      idFn: identity,
      labelFn: (l) => ({ key: languageLabels[l] }),
    });
    this.availableCompanies = availableCompanies;
    this.apiService = apiService;
    this.locale = locale;
    this.dispatch = dispatch;
    this.isLoadingInvite = isLoadingInvite;
    this.showEmailField = showEmailField;
    this.hasAdminInCreatorCompany = hasAdminInCreatorCompany;
    this.selectedUserCompanyItems = selectedUserCompanyItems;
    this.userName = userName;
    this.portfolioName = portfolioName;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get customRoleId(): string {
    return this.customRoleItem.role.customRoleId;
  }

  get roleName(): string {
    return getRoleNameByLanguage(this.customRoleItem.role, this.locale) ?? "";
  }

  get companyName(): string {
    return (
      this.customRoleItem.companies.find(
        (p) => this.customRoleItem.role.orgNo === p.organisationNumber,
      )?.companyName ?? ""
    );
  }

  isSelected(orgNo: string): boolean {
    return this.selectedCompanies.includes(orgNo);
  }

  get createMissingRoleNotice(): TMessage | undefined {
    const companiesLength = this.otherCompanies.length;
    return companiesLength > 0
      ? {
          key: "portfolioContacts.custom-roles-invite-dialog-personal-note",
          params: {
            roleName: this.roleName,
            count: companiesLength,
          },
        }
      : undefined;
  }

  get permissionMessages(): TMessage | undefined {
    return !this.hasAdminInCreatorCompany
      ? {
          key: "portfolioContacts.custom-roles-invite-dialog-permission",
          params: {
            companyName: this.companyName,
            roleName: this.roleName,
          },
        }
      : undefined;
  }

  get availableInvitationCompanies() {
    return this.availableCompanies
      .filter((c) =>
        this.customRoleItem.companies?.some((p) => p.organisationNumber === c.organisationNumber),
      )
      .filter(
        (c) =>
          !this.selectedUserCompanyItems.some((p) => p.organisationNumber === c.organisationNumber),
      );
  }

  get otherCompanies() {
    return this.availableCompanies.filter(
      (c) =>
        !this.customRoleItem.companies?.some((p) => p.organisationNumber === c.organisationNumber),
    );
  }

  get isDisabledSubmit(): boolean {
    return this.selectedCompanies.length === 0 || this.email.isEmpty || !this.email.isValid;
  }

  get getMessageForAllCompaniesAlreadyShared(): TMessage | undefined {
    return this.otherCompanies.length === 0 && this.availableInvitationCompanies.length === 0
      ? {
          key: "contacts.custom-role.already-in-all-companies-in-portfolio",
          params: {
            name: this.userName,
            roleName: this.roleName,
            portfolio: this.portfolioName,
          },
        }
      : undefined;
  }

  toggleSelected(orgNo: string) {
    if (this.isSelected(orgNo)) {
      this.selectedCompanies = this.selectedCompanies.filter((c) => c !== orgNo);
    } else {
      this.selectedCompanies.push(orgNo);
    }
  }

  onCancel() {
    this.dispatch([{ type: "closeDialog" }]);
  }

  selectAllCompanies() {
    this.availableInvitationCompanies.map((c) => this.selectedCompanies.push(c.organisationNumber));
  }

  deselectAllCompanies() {
    this.selectedCompanies = [];
  }

  enableAllCompanies() {
    this.enableCompanies(this.otherCompanies.map((c) => c.organisationNumber));
  }

  enableCompany(orgNo: string) {
    this.enableCompanies([orgNo]);
  }

  enableCompanies(orgNos: string[]) {
    this.enablingCompany = orgNos.length === 1 ? orgNos[0] : "all";
    const firstCompany = this.availableCompanies.find((p) => p.organisationNumber === orgNos[0]);
    const body: AddCompaniesToCustomRoleRequest = {
      customRoleId: this.customRoleId,
      companies: orgNos,
    };
    this.dispatch([
      portfolioApi.addCompaniesToCustomRole({
        body,
        onSuccess: () => [
          showAlert({
            key: "portfolioContacts.custom-roles.add-companies.success",
            params: {
              roleName: this.roleName,
              companyName: firstCompany?.companyName,
              count: body.companies.length,
            },
          }),
          thunk(() => this.onCompaniesEnabled(orgNos)),
        ],
        onFailure: () => [thunk(() => this.onFailedToEnableCompanies())],
      }),
    ]);
  }

  onCompaniesEnabled(orgNos: string[]) {
    this.otherCompanies
      .filter((c) => orgNos.includes(c.organisationNumber))
      .forEach((c) => {
        this.customRoleItem.companies.push({
          organisationNumber: c.organisationNumber,
          companyName: c.companyName,
        });
        this.toggleSelected(c.organisationNumber);
      });
    this.enablingCompany = undefined;
  }

  onFailedToEnableCompanies() {
    this.dispatch([showGeneralError()]);
    this.enablingCompany = undefined;
  }

  *inviteCustomRole() {
    this.isLoadingInvite = true;
    try {
      yield this.apiService.inviteAdministratorOrCustomeRole({
        email: this.email.validatedValue,
        language: this.language.value,
        inviteTo: this.selectedCompanies,
        inviteToRoleId: this.customRoleId,
      });
      this.onCancel();
      this.dispatch([
        {
          type: "show-alert",
          messageType: "success",
          message: {
            key: "invite-user.success-message",
          },
        },
      ]);
    } catch (e) {
      this.onCancel();
      this.dispatch([
        {
          type: "show-alert",
          messageType: "error",
          message: {
            key: "general-error",
          },
        },
      ]);
    } finally {
      this.isLoadingInvite = false;
    }
  }
}

function findCustomRoleLanguage(
  role: CustomRole,
  language: Language,
): CustomRoleLanguage | undefined {
  return role.customRoleLanguages.find((l) => l.language === language);
}

export function filterCustomRoleUsers({
  users,
  companies,
  freetextFilter,
  companyFilter,
}: {
  users: UserItem[];
  companies: CompanyItem[];
  freetextFilter: string;
  companyFilter?: CompanyItem | null;
}): UserItem[] {
  const query = freetextFilter.toLowerCase().trim();
  const filteredCompanies = companies.filter(
    (c) =>
      c.companyName.toLowerCase().includes(query) ||
      c.organisationNumber === companyFilter?.organisationNumber,
  );
  return users.filter((user) => {
    if (companyFilter) {
      return (
        companyFilter.organisationNumber === user.orgNo && user.name.toLowerCase().includes(query)
      );
    }
    return (
      !query ||
      user.name.toLowerCase().includes(query) ||
      filteredCompanies.some((c) => c.organisationNumber === user.orgNo)
    );
  });
}

export type CustomRoleTableUserRow = {
  externalId: string;
  name: string;
  email?: string;
  lastLogin?: string;
  companies: CompanyItem[];
};

export function prepCustomRoleUsersTableData({
  users,
  companies,
}: {
  users: UserItem[];
  companies: CompanyItem[];
}): CustomRoleTableUserRow[] {
  return Object.values(groupBy(users, "externalId")).map<CustomRoleTableUserRow>((users) => ({
    externalId: users[0].externalId,
    name: users[0].name,
    email: users[0].email,
    lastLogin: maxBy(
      users.filter((u) => u.lastLogin).map<string>((u) => u.lastLogin!),
      (lastLogin) => toDate(lastLogin).getTime(),
    ),
    companies: companies.filter((c) => users.some((u) => c.organisationNumber === u.orgNo)),
  }));
}

export function getRoleNameByLanguage(role: CustomRole, language: Language): string {
  return findCustomRoleLanguage(role, language)?.name ?? role.customRoleName;
}

export function getDescriptionByLanguage(role: CustomRole, language: Language): string | undefined {
  return findCustomRoleLanguage(role, language)?.description;
}
